import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { generateFormFromDocument, getFormGeneration } from 'services/taskService'
import { redirect } from 'utils/paths'
import { ACTIVE_STATUSES, MAX_POLLS, POLL_INTERVAL_MS, builderLinkFor, questionCountText } from './formGeneration'

const FormGenerationContext = createContext(null)

export const useGenerationStore = () => {
  const [entries, setEntries] = useState({})
  const timers = useRef(new Map())
  const watchers = useRef(new Map())
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    const timerMap = timers.current
    return () => {
      alive.current = false
      timerMap.forEach(timer => clearTimeout(timer))
      timerMap.clear()
    }
  }, [])

  const update = useCallback((id, patch) => {
    setEntries(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }))
  }, [])

  const isWatched = id => (watchers.current.get(id) || 0) > 0

  const announce = useCallback(generation => {
    if (isWatched(generation.id)) return
    if (generation.status === 'ready') {
      toast.success(
        `Your draft form is ready: ${generation.formTemplateName || 'Untitled form'}, ${questionCountText(generation.questionCount)}. Select this message to check and publish it.`,
        {
          autoClose: false,
          onClick: () => {
            const link = builderLinkFor(generation)
            if (link) redirect(link)
          }
        }
      )
    } else if (generation.status === 'failed') {
      toast.error(generation.error || 'We could not turn your file into a form.')
    }
  }, [])

  const poll = useCallback(id => {
    let polls = 0
    const tick = async () => {
      timers.current.delete(id)
      polls += 1
      try {
        const res = await getFormGeneration(id)
        if (!alive.current) return
        const next = res?.data?.data
        if (next) {
          update(id, { generation: { ...next, id: next.id || id } })
          if (!ACTIVE_STATUSES.includes(next.status)) {
            announce({ ...next, id: next.id || id })
            return
          }
        }
      } catch (e) {
        if (!alive.current) return
      }
      if (polls >= MAX_POLLS) {
        update(id, { timedOut: true })
        return
      }
      timers.current.set(id, setTimeout(tick, POLL_INTERVAL_MS))
    }
    timers.current.set(id, setTimeout(tick, POLL_INTERVAL_MS))
  }, [update, announce])

  const start = useCallback(async ({ file, buildingPassportId, organisationId, targetType, targetId }) => {
    const res = await generateFormFromDocument({ file, buildingPassportId, organisationId, targetType, targetId })
    const data = res?.data?.data || {}
    const id = data.id
    if (!id) throw new Error('missing generation id')
    update(id, { generation: { status: 'queued', ...data, sourceFilename: data.sourceFilename || file.name }, timedOut: false })
    poll(id)
    return id
  }, [update, poll])

  const watch = useCallback(id => {
    if (!id) return () => {}
    watchers.current.set(id, (watchers.current.get(id) || 0) + 1)
    return () => {
      const left = (watchers.current.get(id) || 1) - 1
      if (left > 0) watchers.current.set(id, left)
      else watchers.current.delete(id)
    }
  }, [])

  return useMemo(() => ({ entries, start, watch }), [entries, start, watch])
}

export const FormGenerationProvider = ({ children }) => {
  const store = useGenerationStore()
  return <FormGenerationContext.Provider value={store}>{children}</FormGenerationContext.Provider>
}

export const useFormGenerationStore = () => {
  const shared = useContext(FormGenerationContext)
  const local = useGenerationStore()
  return shared || local
}
